/**
 * WarrantTracker.js — the warrant register, laid out like Fund Positions
 * =======================================================================
 * Brian, 1 October 2026: *"i want the warrant tracker to look like the
 * positions dashboard but specifically built for metrics that matter to
 * warrants."*
 *
 * So it is built out of the Dashboard's Fund Positions table: the same card,
 * the same seven-figure strip, the same captions, the same sortable grouped
 * table with a filter box. It uses that table's own stylesheet and class
 * names rather than a copy of them, so the two pages cannot drift apart.
 * What changes is the columns, and every one of them answers a question
 * about a warrant rather than about a position:
 *
 *   - **Underlying Price** — the common stock the warrant exercises into,
 *     priced the way NAV prices it, so it is the figure the positions table
 *     shows for the same stock, with its date.
 *   - **Underlying vs Strike** — the common's price against the strike.
 *     Positive is in the money. This is the column that says how far a stock
 *     has to move before a warrant is worth anything.
 *   - **Intrinsic Value** — what the series would realise exercised today,
 *     floored at nil. The page used to show "if exercised today" unfloored, so
 *     an out-of-the-money warrant read as a large negative figure.
 *   - **Aggregate Strike** — strike × warrant shares, the cash a cash
 *     exercise would take.
 *   - **Expiry** — the date and the days left, amber inside six months.
 *
 * The groups are what a reader sorts warrants into before anything else: in
 * the money, out of the money, and the ones nothing could measure. That last
 * group says on each row why not, on the face of the table rather than in a
 * tooltip (§0.37). Series that are no longer held are hidden the way the
 * Dashboard hides a bank account holding nothing: counted in a caption, and
 * one click brings them back.
 *
 * What it still refuses to do:
 *
 *   - **No mark.** Intrinsic value is what exercising would realise, with no
 *     time value. It is not in NAV, and the book carries every warrant at nil
 *     (Brian, 22 September 2026: *"the warrants are marked at zero"*). The
 *     caption says both, with the carrying value read off the lots.
 *   - **No partial total.** A dollar total is "—" when any series in it has no
 *     dollar rate, rather than a total of the ones that converted (§0.30).
 *   - **No long text in tooltips.** The expiry formulas, the strike's source,
 *     the exercises and the disagreements between records are in a detail row,
 *     opened by clicking the series.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { Button, Card, EmptyState, PageHeader } from "../components/ui";
import t from "../components/theme";
import { number } from "../components/positionRollup";
import "../components/DashboardPositions.css";
import "./WarrantTracker.css";

const format = (value, digits = 2) => {
  const n = number(value);
  if (n === null) return "—";
  const text = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return n < 0 ? `(${text})` : text;
};
// A share count is a whole number on most rows and not on all of them: SRx
// Health's warrants were divided by sixty in its July 2026 reverse split.
// Rounding 16,666.6667 to 16,667 would state a holding the book does not have.
const shares = value => {
  const n = number(value);
  if (n === null) return "—";
  return format(n, Number.isInteger(n) ? 0 : 2);
};
// Negative in brackets, as on Fund Positions: "(31.40%)", never "(31.40)%".
const percent = value => {
  const n = number(value);
  if (n === null) return null;
  const text = `${format(Math.abs(n) * 100)}%`;
  return n < 0 ? `(${text})` : text;
};
const usd = value => (number(value) === null ? "—" : `$${format(value)}`);
const missing = v => v === null || v === undefined || v === "";

// The register's state in the words a reader uses. For a series that left the
// book, the word somebody wrote on it is the answer ("Sold", "Removed").
const STATUS = {
  on_the_book: "Held",
  terms_only: "Not on the book",
  position_only: "No terms on file",
  exercised_in_full: "Exercised",
  not_held: "No longer held",
};
const LIVE_STATES = ["on_the_book", "terms_only", "position_only"];

const GROUPS = ["In the money", "Out of the money", "Not measured", "No longer held"];

const isLive = row => LIVE_STATES.includes(row.state) && number(row.shares_outstanding) > 0;
const groupOf = row => {
  if (!isLive(row)) return "No longer held";
  const e = row.economics;
  if (!e || number(e.moneyness) === null) return "Not measured";
  return e.in_the_money ? "In the money" : "Out of the money";
};
const whyNotMeasured = row => row.economics?.not_measured_because
  || (number(row.shares_outstanding) ? "not measured" : "no warrant share count is recorded");

const rowKey = (row, index) => `${row.company_key}|${row.series}|${row.issue_date || ""}|${index}`;

// A missing figure sorts to the bottom in both directions, as on Fund
// Positions: ordering a "—" as zero would put a series nothing could measure
// among the ones worth nothing, which is a claim the dash never made.
const compareBy = (column, dir) => (a, b) => {
  const av = column.get(a), bv = column.get(b);
  if (missing(av) || missing(bv)) return missing(av) && missing(bv) ? 0 : missing(av) ? 1 : -1;
  const d = column.numeric ? av - bv : String(av).localeCompare(String(bv), "en");
  return dir === "asc" ? d : -d;
};

const COLUMNS = [
  { label: "Issuer", get: r => r.company },
  { label: "Series", get: r => r.series },
  { label: "CCY", get: r => r.currency },
  { label: "Warrant Shares", numeric: true, get: r => number(r.shares_outstanding) },
  { label: "Strike (Local)", numeric: true, get: r => number(r.strike_price) },
  { label: "Underlying Price (Local)", numeric: true, get: r => number(r.economics?.underlying?.price) },
  { label: "Underlying vs Strike", numeric: true, get: r => number(r.economics?.moneyness) },
  { label: "Intrinsic Value (Local)", numeric: true, get: r => number(r.economics?.intrinsic_value) },
  { label: "Intrinsic Value (USD)", numeric: true, get: r => number(r.economics?.intrinsic_value_usd) },
  { label: "Aggregate Strike (USD)", numeric: true, get: r => number(r.economics?.aggregate_strike_usd) },
  { label: "Expiry", numeric: true, get: r => number(r.days_to_expiry) },
  { label: "Book Status", get: r => statusOf(r) },
];
const DEFAULT_SORT = "Intrinsic Value (USD)";
const MONEYNESS = COLUMNS.find(c => c.label === "Underlying vs Strike");
const SHARES = COLUMNS.find(c => c.label === "Warrant Shares");

function statusOf(row) {
  if (row.state === "not_held" && row.terms_status) return row.terms_status;
  return STATUS[row.state] || row.state;
}

// The total of a USD column over a group, or null when any counted row in it
// has no figure: a total of the rows that converted would leave the others out
// without saying so.
const total = (rows, get) => {
  const counted = rows.filter(r => r.counted_in_total !== false);
  if (!counted.length) return 0;
  const values = counted.map(get);
  return values.some(v => v === null) ? null : values.reduce((s, v) => s + v, 0);
};

function Detail({ row }) {
  const e = row.economics;
  const u = e?.underlying;
  const items = [];
  if (row.expiry_method) items.push(["Expiry", row.expiry_method]);
  if (row.initial_exercise_date) items.push(["Exercisable from", row.initial_exercise_date]);
  if (row.strike_price_method) items.push(["Strike", row.strike_price_method]);
  if (u) {
    items.push(["Underlying", `${u.ticker} at ${u.currency || ""} ${format(u.price, 4)} on ${u.price_date} (${u.price_source})`
      + (u.listings_considered > 1 ? `, the freshest of ${u.listings_considered} listings for this issuer in the strike's currency` : "")]);
  }
  if (e?.not_measured_because) items.push(["Not measured", e.not_measured_because]);
  if (row.currency_source && row.currency_source !== "warrant security") {
    items.push(["Currency", `${row.currency}, read from ${row.currency_source}`]);
  }
  if (row.shares_issued !== null && row.shares_issued !== undefined
      && String(row.shares_issued) !== String(row.shares_outstanding)) {
    items.push(["Issued", `${shares(row.shares_issued)} warrant shares`]);
  }
  if ((row.exercises_applied || []).length) {
    items.push(["Exercised", row.exercises_applied.map(x =>
      `${x.notice_date || "undated"}${x.shares ? `, ${shares(x.shares)} shares` : ""}${x.price ? ` at ${x.price}` : ""}`).join(" · ")]);
  }
  if ((row.notices_not_applied || []).length) {
    items.push(["Exercise notices not applied", row.notices_not_applied.map(x =>
      `${x.notice_date || "undated"}${x.shares ? `, ${shares(x.shares)} shares` : ""}`).join(" · ")]);
  }
  (row.divergences || []).forEach(d => items.push([`Records disagree: ${d.field}`,
    `terms register ${String(d.deal_warrant)}, instrument row ${String(d.security_definition)}`]));
  if (row.exercise_conditions) items.push(["Conditions", row.exercise_conditions]);
  if (row.notes) items.push(["Notes", row.notes]);
  if (row.reads_as) items.push(["Record", row.reads_as]);
  return <dl className="warrant-detail">{items.map(([label, text], i) =>
    <React.Fragment key={i}><dt>{label}</dt><dd>{text}</dd></React.Fragment>)}</dl>;
}

function Finding({ title, rows, why }) {
  const [open, setOpen] = useState(false);
  if (!rows || rows.length === 0) return null;
  return <div className="warrant-finding">
    <button type="button" aria-expanded={open} onClick={() => setOpen(v => !v)}>
      {open ? "▾" : "▸"} {title} <span className="warrant-count">({rows.length})</span>
    </button>
    {open && <div className="warrant-finding-body">
      <p>{why}</p>
      <ul>{rows.map((row, i) => <li key={i}>
        <b>{row.company}</b>{row.series ? ` — ${row.series}` : ""}
        {row.issue_date ? `, issued ${row.issue_date}` : ""}
        {number(row.shares_outstanding) ? ` · ${shares(row.shares_outstanding)} warrant shares` : ""}
        {row.strike_price !== null && row.strike_price !== undefined ? ` at ${row.currency || "?"} ${format(row.strike_price, 4)}` : ""}
        {row.terms_status ? ` · marked ${row.terms_status}` : ""}
      </li>)}</ul>
    </div>}
  </div>;
}

export default function WarrantTracker() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [sort, setSort] = useState({ label: DEFAULT_SORT, dir: "desc" });
  const [showGone, setShowGone] = useState(false);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/reports/warrant-register/");
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail
        || "Could not load the warrant register.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const series = useMemo(() => (report?.series || []).map((row, i) => ({ ...row, key: rowKey(row, i) })), [report]);
  const summary = report?.exercise_economics || {};
  const holding = report?.holding || {};
  const carrying = report?.carrying_value || {};
  const findings = report?.findings || {};

  const searching = query.trim() !== "";
  const needle = query.trim().toLowerCase();
  const matches = row => !searching || [row.company, row.series, row.currency, statusOf(row),
    groupOf(row), row.economics?.underlying?.ticker].some(v => String(v || "").toLowerCase().includes(needle));
  const gone = series.filter(row => groupOf(row) === "No longer held");
  const liveIssuers = new Set(series.filter(r => isLive(r) && r.counted_in_total !== false).map(r => r.company_key)).size;
  const withheld = series.filter(r => isLive(r) && r.counted_in_total === false);
  const spellings = (report?.by_company || []).filter(c => (c.spellings || []).length > 1);

  const sorted = COLUMNS.find(c => c.label === sort.label) || COLUMNS.find(c => c.label === DEFAULT_SORT);
  // Ties — every out-of-the-money series is worth nil exercised — break on how
  // near the money a series is, then on its size, so the default order puts
  // the warrant closest to being worth something first.
  const order = (a, b) => compareBy(sorted, sort.dir)(a, b)
    || compareBy(MONEYNESS, "desc")(a, b) || compareBy(SHARES, "desc")(a, b);

  const nearest = summary.nearest_to_the_money;
  const next = summary.next_expiry;
  const metrics = [
    ["Live Series", format(summary.series_live ?? 0, 0), `${liveIssuers} issuer${liveIssuers === 1 ? "" : "s"}`],
    ["Warrant Shares", shares(holding.shares_outstanding), "a count of shares, not a value"],
    ["In the Money", format(summary.series_in_the_money ?? 0, 0), `of ${summary.series_measured ?? 0} measured${summary.series_not_measured ? ` · ${summary.series_not_measured} not` : ""}`],
    ["Intrinsic Value", usd(summary.intrinsic_value_usd), "in-the-money series, exercised today"],
    ["Exercise Cost, ITM", usd(summary.aggregate_strike_in_the_money_usd), "strike × shares, paid in cash"],
    ["Nearest to the Money", nearest ? percent(nearest.moneyness) : "—", nearest ? `${nearest.company} · ${nearest.series}` : "nothing out of the money"],
    ["Next Expiry", next ? `${format(next.days_to_expiry, 0)} days` : "—", next ? `${next.company} · ${next.expiry_date}` : "no live series has a dated expiry"],
  ];

  const title = `Warrants — ${summary.series_live ?? 0} Live Series`;
  return <div className="portfolio-dashboard warrant-tracker">
    <PageHeader title="Warrant Tracker"
      subtitle={report?.as_of ? `Every warrant series the fund holds, as at ${report.as_of}` : "Every warrant series the fund holds"}
      action={<Button onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</Button>} />

    {error && <Card><div className="warrant-error">{error}</div></Card>}
    {report?.basis?.terms_rows === null && <Card><div className="warrant-error">
      No terms register in this database. {report.basis.terms_register} Every strike and expiry below is therefore missing.
    </div></Card>}

    <Card title={title} noPad action={<input className="positions-search" aria-label="Filter warrants" placeholder="Filter warrants…" value={query} onChange={e => setQuery(e.target.value)} />}>
      <section className="dashboard-positions" style={{ "--positions-border": t.borderLight, "--positions-subtle": t.bgSubtle, "--positions-muted": t.textMuted, color: t.text, fontFamily: t.fontBody }}>
        {loading && !report ? <EmptyState message="Loading the register…" /> : <>
        <div className="positions-metrics">{metrics.map(([label, value, sub]) =>
          <div key={label}><span>{label}</span><strong>{value}</strong><small>{sub}</small></div>)}</div>
        <div className="positions-caption">{report?.as_of && <span>As of {report.as_of} · </span>}One row per warrant series. Warrant shares are the open tax lots, or the terms where the book has no lot. Strike, underlying price and local amounts are in the row currency; USD at the as-of fixing. Click a series for its expiry formula, the strike's source and the underlying used.</div>
        <div className="positions-caption">Underlying vs Strike is the common stock's price against the strike, positive in the money. Intrinsic value is (price − strike) × warrant shares where that is positive, with no time value. Aggregate strike is strike × warrant shares. None of it is a mark or in NAV: the book carries every warrant at {usd(carrying.amount)} across {carrying.open_lots_checked ?? 0} open lots, by ruling (Brian, 22 September 2026: <i>"the warrants are marked at zero"</i>).</div>
        {carrying.agrees_with_the_ruling === false && <div className="positions-caption warrant-alarm">The ledger carries cost on a warrant, which disagrees with the ruling: {(carrying.carried_for_money || []).map(e => `${e.company} — ${e.series} (${e.issue_date || "no date"}): ${e.currency || ""} ${format(e.cost_on_the_book)}`).join("; ")}.</div>}
        {(summary.unconverted_currencies || []).length > 0 && <div className="positions-caption positions-warn">No USD rate for {summary.unconverted_currencies.join(", ")} at {report.as_of}: those series show no USD figure, and a USD total that includes one shows "—" rather than leaving it out.</div>}
        {withheld.length > 0 && <div className="positions-caption positions-warn">{withheld.length} series {withheld.length === 1 ? "is" : "are"} on {withheld.length === 1 ? "its line" : "their lines"} and kept out of every total: their records do not join, so they may be the same warrant twice.</div>}
        {gone.length > 0 && !searching && <div className="positions-caption">{showGone
          ? <>Showing {gone.length} series no longer held. <button type="button" className="positions-link" onClick={() => setShowGone(false)}>Hide them</button></>
          : <>{gone.length} series no longer held — sold, exercised or removed — {gone.length === 1 ? "is" : "are"} hidden. <button type="button" className="positions-link" onClick={() => setShowGone(true)}>Show {gone.length === 1 ? "it" : "them"}</button></>}
        </div>}
        <div className="positions-scroll" tabIndex={0} role="region" aria-label="Warrants table">
          <table><thead><tr>{COLUMNS.map(col => {
            const active = col.label === sorted.label;
            const nextDir = active ? (sort.dir === "asc" ? "desc" : "asc") : (col.numeric ? "desc" : "asc");
            return <th key={col.label} scope="col" className={col.numeric ? "numeric" : ""}
              aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
              <button type="button" className="positions-sort" onClick={() => setSort({ label: col.label, dir: nextDir })}
                title={`Sort by ${col.label}, ${nextDir === "asc" ? "smallest" : "largest"} first`}>
                {col.label}<span aria-hidden="true" className="positions-arrow">{active ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
              </button></th>;
          })}</tr></thead>
            <tbody>{GROUPS.map(group => {
              if (group === "No longer held" && !showGone && !searching) return null;
              const rows = series.filter(r => groupOf(r) === group && matches(r)).sort(order);
              if (!rows.length) return null;
              const groupShares = total(rows, r => number(r.shares_outstanding) ?? 0);
              const groupIntrinsic = total(rows, r => number(r.economics?.intrinsic_value_usd));
              const groupStrike = total(rows, r => number(r.economics?.aggregate_strike_usd));
              const live = group !== "No longer held";
              return <React.Fragment key={group}>
                <tr className="positions-group">
                  <td colSpan={3}><button type="button" onClick={() => setCollapsed(s => ({ ...s, [group]: !s[group] }))} aria-expanded={!collapsed[group]}>{collapsed[group] ? "▸" : "▾"} {group} ({rows.length})</button></td>
                  <td className="numeric">{live ? shares(groupShares) : ""}</td>
                  <td colSpan={4} />
                  <td className="numeric">{live && group !== "Not measured" ? format(groupIntrinsic) : ""}</td>
                  <td className="numeric">{live ? format(groupStrike) : ""}</td>
                  <td colSpan={2} />
                </tr>
                {!collapsed[group] && rows.map(row => {
                  const e = row.economics || {};
                  const u = e.underlying;
                  const m = number(e.moneyness);
                  const days = number(row.days_to_expiry);
                  const expiryClass = days === null ? "" : days < 0 ? " warrant-expired" : days <= 180 ? " positions-stale" : "";
                  const isOpen = !!open[row.key];
                  return <React.Fragment key={row.key}>
                    <tr className={row.counted_in_total === false ? "warrant-withheld" : undefined}>
                      <th scope="row">
                        <button type="button" className="warrant-toggle" aria-expanded={isOpen}
                          onClick={() => setOpen(s => ({ ...s, [row.key]: !s[row.key] }))}>
                          <span aria-hidden="true">{isOpen ? "▾" : "▸"}</span> {row.company || "—"}
                        </button>
                      </th>
                      <td className="positions-description">{row.series || "—"}
                        {row.issue_date && <small className="positions-asat">issued {row.issue_date}</small>}
                        {(row.is_prefunded || row.cashless_exercise || row.counted_in_total === false) && <small className="warrant-flags">
                          {[row.is_prefunded && "pre-funded", row.cashless_exercise && "cashless", row.counted_in_total === false && "not in total"].filter(Boolean).join(" · ")}
                        </small>}
                      </td>
                      <td>{row.currency || "—"}</td>
                      <td className="numeric">{shares(row.shares_outstanding)}
                        {!live && number(row.shares_issued) ? <small className="positions-asat">of {shares(row.shares_issued)} issued</small> : null}</td>
                      <td className="numeric">{format(row.strike_price, 4)}</td>
                      {/* The price and the date it is as at, as on Fund
                          Positions, and the ticker it was read off, because
                          the common is found by the issuer's name and the
                          match is worth being able to check at a glance. */}
                      <td className="numeric warrant-underlying">{u ? <>{format(u.price, 4)}
                        <small className="positions-asat">{u.ticker}</small>
                        <small className={`positions-asat${u.price_date && report?.as_of && (new Date(report.as_of) - new Date(u.price_date)) / 86400000 > 7 ? " positions-stale" : ""}`}>{u.price_date}</small></> : "—"}</td>
                      {live && m === null
                        ? <td className="warrant-reason" colSpan={3}>{whyNotMeasured(row)}</td>
                        : <>
                          <td className={`numeric${m > 0 ? " warrant-itm" : ""}`}>{m === null ? "—" : row.is_prefunded ? "pre-funded" : percent(m)}</td>
                          <td className="numeric">{format(e.intrinsic_value)}</td>
                          <td className="numeric positions-value">{format(e.intrinsic_value_usd)}</td>
                        </>}
                      <td className="numeric">{format(e.aggregate_strike_usd)}</td>
                      <td className={`numeric${expiryClass}`}>{row.expiry_date
                        ? <>{row.expiry_date}<small className="positions-asat">{days < 0 ? "expired" : `${format(days, 0)} days`}</small></>
                        : row.expiry_method ? <span className="warrant-formula">by formula</span> : "—"}</td>
                      <td className="positions-method">{statusOf(row)}</td>
                    </tr>
                    {isOpen && <tr className="warrant-detail-row"><td colSpan={COLUMNS.length}><Detail row={row} /></td></tr>}
                  </React.Fragment>;
                })}
              </React.Fragment>;
            })}
            {!series.some(r => matches(r) && (showGone || searching || groupOf(r) !== "No longer held")) &&
              <tr><td colSpan={COLUMNS.length} className="positions-empty">{series.length ? "No matching warrants." : "No warrant series on the book."}</td></tr>}
            </tbody>
          </table>
        </div>
        </>}
      </section>
    </Card>

    {report && <Card title="What needs fixing" subtitle="An empty list means the check ran and found nothing, not that it was skipped.">
      <Finding title="A status this page does not recognise" rows={findings.status_not_understood}
        why="DealWarrant.status is free text, so a spelling the register does not know is counted as held rather than dropped: a typo must never delete a holding. The cost runs the other way, and a series really sold under an unrecognised word keeps inflating the total until the word is fixed." />
      <Finding title="Terms recorded, no position on the book" rows={findings.terms_recorded_no_position}
        why="The strike and expiry are on file and there is no instrument row and no tax lot, so no report that reads positions can see these warrants. Read the certificate to confirm the series was issued and is still held, then raise the security and the lot." />
      <Finding title="Position on the book, no terms recorded" rows={findings.position_no_terms}
        why="A warrant is held and its strike and expiry are recorded nowhere the engine reads, so it cannot be valued and its expiry cannot be warned about." />
      <Finding title="Two records of one warrant disagree" rows={findings.records_disagree}
        why="On a warrant with a down-round ratchet the certificate states the price on the day it was signed, not today's. The issuer's own SEC filings settle a strike that has moved." />
      <Finding title="Records nothing joins" rows={(findings.records_nothing_joins || []).map(e => ({ company: e.company_key, series: `${e.deal_warrant_ids.length} terms row(s), ${e.security_ids.length} instrument row(s)` }))}
        why="These may describe the same warrant twice, so they are on their own lines and kept out of every total. Joining them needs the certificate number or the share count written into both records." />
      <Finding title="Past expiry with a position still open" rows={findings.expired_and_still_open}
        why="Either the warrant lapsed unexercised and the lot should be closed, or the expiry recorded is wrong." />
      <Finding title="No expiry date recorded" rows={findings.no_expiry_recorded}
        why="Not by itself a defect. Several of these run to an anniversary of a stockholder approval or a Bolagsverket registration that is not on file, so the formula is in the series' detail row." />
      <Finding title="One issuer, several spellings on the records" rows={spellings.map(c => ({ company: c.company, series: c.spellings.join(" / ") }))}
        why="Folded to one company for every figure on this page; the records themselves should be made to agree." />
      {(report.what_this_report_cannot_say || []).length > 0 && <Finding title="What this page cannot tell you"
        rows={report.what_this_report_cannot_say.map(line => ({ company: line }))} why="" />}
    </Card>}
  </div>;
}
